import { useMutation, useQuery } from "@apollo/client";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EastIcon from "@mui/icons-material/East";
import Visibility from "@mui/icons-material/Visibility";
import { Alert, Button, IconButton, Skeleton, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  EditMode,
  FormState,
} from "components/Document/AddEditDocumentDialog";
import { FullScreenDocumentPreview } from "components/Document/shared/FullScreenDocumentPreview";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { isManifestDocumentReviewed } from "encamp-shared/src/waste/manifestSourcePriority";
import {
  DiscrepancyKind,
  DiscrepancyStatus,
  DocumentType,
  SubmissionStatus,
  WasteManifestSource,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useFeatureFlags, useStoredFeatureFlags } from "hooks/useFeatureFlags";
import { useProcessedManifestSuspense } from "hooks/useProcessedManifest";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useTenant } from "hooks/useTenant";
import { useWasteLinesUnified } from "hooks/useWasteLinesUnified";
import {
  PropsWithChildren,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { ManifestDetailDialog } from "routes/Staff/WasteFulfillment/ManifestReview/ManifestDetailDialog";

import {
  ATTACH_DOCUMENT_TO_MANIFEST,
  CREATE_EXCEPTION_REPORT_SUBMISSION,
  DETACH_DOCUMENT_FROM_MANIFEST,
  PROCESSED_MANIFEST_DOCUMENTS_QUERY,
  PROCESSED_MANIFESTS_QUERY,
} from "./api";
import { COMPARE_PARAM_KEY } from "./CompareSourcesDialog";
import { encodeCompareParam } from "./CompareSourcesDialog.helpers";
import { daysUntilEarliestEpaCorrectionDeadline } from "./CompliancePane.helpers";
import {
  BULK_MANIFEST_DISCREPANCIES_QUERY,
  type DiscrepancySectionKey,
  DiscrepancyReviewDialog,
} from "./DiscrepancyReviewDialog";
import { EditManifestDialog } from "./EditManifestDialog";
import { summarizeManagementMethods } from "./managementMethodDisplay";
import { ManifestLinkedShipmentCard } from "./ManifestLinkedShipmentCard";
import { ManifestPdfRail } from "./ManifestPdfRail";
import { ManifestReviewDialog } from "./ManifestReviewDialog";
import { defaultSource, SOURCE_LABEL } from "./manifestSourceDisplay";
import { ManifestUploadDialog } from "./ManifestUploadDialog";
import { ProofOfContactDialog } from "./ProofOfContactDialog";
import { ReplaceManifestDocumentDialog } from "./ReplaceManifestDocumentDialog";
import { ComplianceSection } from "./sections/ComplianceSection";
import { DiscrepanciesSection } from "./sections/DiscrepanciesSection";
import { buildDiscrepancyTally } from "./sections/discrepancyBreakdown";
import { DocumentsSection } from "./sections/DocumentsSection";
import { GeneratorFacilitySection } from "./sections/GeneratorFacilitySection";
import { ReceivingTsdfSection } from "./sections/ReceivingTsdfSection";
import { SectionCard } from "./sections/SectionCard";
import { SectionHeader } from "./sections/SectionHeader";
import { SummarySection } from "./sections/SummarySection";
import {
  buildSummaryItems,
  buildTimelineEvents,
  shouldWarnMissingEManifestSiteAccess,
} from "./sections/summaryStatus";
import { TimelineSection } from "./sections/TimelineSection";
import { TransportersSection } from "./sections/TransportersSection";
import { ManifestPdfDocument } from "./sections/types";
import { SourceLetterBox } from "./SourceLetterBox";
import { StickyManifestHeader } from "./StickyManifestHeader";
import { useManifestDiscrepancyFields } from "./useManifestDiscrepancyFields";
import { useProcessManifestDocuments } from "./useProcessManifestDocuments";
import { useScrollToSectionParam } from "./useScrollToSectionParam";

export interface ManifestDetailSectionProps extends PropsWithChildren {
  processedManifestId: string | undefined;
  onClose: () => void;
  onManifestIdChange?: (newManifestId: string) => void;
  /** Explicit tenant for cross-tenant (staff) callers off a tenant-scoped route. */
  tenantId?: string;
}

/**
 * Directional separator in the Generator → Transporters → Receiving flow row.
 * Down arrow when the rail forces a single column; right arrow side-by-side,
 * hidden when the row wraps to a column on small screens.
 */
const FlowArrow = ({ showRail }: { showRail: boolean }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    sx={{
      color: "text.disabled",
      display: showRail ? "flex" : { xs: "none", md: "flex" },
    }}
  >
    {showRail ? <ArrowDownwardIcon /> : <EastIcon />}
  </Stack>
);

export const ManifestDetailSection = ({
  processedManifestId,
  onClose,
  children,
  tenantId: tenantIdProp,
}: ManifestDetailSectionProps) => {
  const { tenantId: contextTenantId } = useTenant();
  // Staff callers (cross-tenant queue) pass the row's tenant explicitly; customer
  // callers fall back to their route/context tenant.
  const tenantId = tenantIdProp ?? contextTenantId;
  const { featureFlags } = useFeatureFlags();
  const { featureFlags: storedFlags } = useStoredFeatureFlags();
  const alerts = useAlerts();
  const documentsSectionRef = useScrollToSectionParam("documents");
  const { processedManifest: manifest, refetch: refetchManifest } = useProcessedManifestSuspense(
    processedManifestId,
    tenantId
  );
  const { data: wasteLinesUnified } = useWasteLinesUnified(
    {
      search: `tenantId:${tenantId} processedManifestId:${processedManifestId}`,
      // Fetch the manifest's full line set (not the default first page) so the
      // whole-manifest rollups below — the edited flag and the Receiving card's
      // management-method summary — see every line. A single manifest's line
      // count is bounded, so this stays cheap.
      pageSize: 500,
    },
    { skip: !processedManifestId || !tenantId }
  );
  const { isStaff } = useCurrentUser();
  const showDiscrepancyCounts = isStaff;
  const showCompliance = showDiscrepancyCounts;
  // The Discrepancies card is customer-visible on manifest-views-v2 tenants
  // (staff resolve that flag on via its staff override). Customers get the
  // read-only pending/resolved view; the staff-only extras (per-source values,
  // compliance card, Review all) stay behind showDiscrepancyCounts / isStaff.
  const showDiscrepancyCard = !!featureFlags[FeatureFlagKey.ManifestViewsV2];
  const showExceptionReporting =
    showCompliance && featureFlags[FeatureFlagKey.WasteExceptionReporting];
  // Stored waste-module (not staff-overridden) — matches the backend ingest gate.
  const showSourceDocument = isStaff && !!storedFlags[FeatureFlagKey.WasteModule];

  const availableSources = useMemo(() => manifest?.availableSources ?? [], [manifest]);
  // No more source switching — values default to the primary source (falling
  // back per-field to the next available source in the hierarchy).
  const primarySource = useMemo(
    () => defaultSource(manifest?.primarySource, availableSources),
    [manifest?.primarySource, availableSources]
  );

  // A document pillar exists but the backend excluded it from availableSources
  // — i.e. its extracted data hasn't been human-reviewed (the only reason a
  // present pillar is omitted). Drives the staff-only needs-review icon.
  const documentDataUnreviewed =
    !!manifest?.documentManifestId &&
    !availableSources.includes(WasteManifestSource.ManifestDocument);

  // Warn (all users) when an overdue e-Manifest can't be pulled without RCRA
  // site-manager access — it only surfaces in the 90-day public export.
  const warnMissingEManifestSiteAccess = shouldWarnMissingEManifestSiteAccess({
    isUniformHazardousManifest: manifest?.isUniformHazardousManifest,
    reportedWasteGeneratorStatus: manifest?.reportedWasteGeneratorStatus,
    hasEManifest: manifest?.isEManifest,
    escalation: manifest?.eManifestComplianceEscalation,
    siteManagerAccess: manifest?.siteManagerAccess,
  });

  const hasTransporters = (manifest?.wasteTransporters?.length ?? 0) > 0;

  // Per-source values (drives source-switched header fields) + disagreement
  // sets (Group 4 warning icons). Skipped for non-staff / flag-off callers.
  const { valuesByField, disagreementFields } = useManifestDiscrepancyFields(processedManifestId, {
    skip: !showDiscrepancyCounts,
  });

  const { fetchAndHandleDownload, clearDownloadLink } = useDocumentDownload();
  const {
    data: documentsData,
    error: documentsError,
    refetch: refetchDocuments,
    loading: documentsLoading,
  } = useQuery(PROCESSED_MANIFEST_DOCUMENTS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} id:${processedManifestId}`,
    },
    // Fetch for all users — the Documents section is customer-visible, so this
    // can't be gated on the staff-only showDiscrepancyCounts (doing so left the
    // section empty for customers).
    skip: !tenantId || !processedManifestId,
  });
  const manifestDocuments = documentsData?.processedManifests?.items?.[0]?.documents;
  // Loads with the drawer's documents query (not the list rows) — the list never
  // renders the PDF, so there's no reason to fetch it for every row.
  const manifestPdf = documentsData?.processedManifests?.items?.[0]?.manifestPdf ?? null;
  const exceptionReportDocuments =
    documentsData?.processedManifests?.items?.[0]?.exceptionReportSubmission?.documents;
  const additionalManifestDocuments =
    documentsData?.processedManifests?.items?.[0]?.additionalManifestDocuments;

  // The manifest's discrepancy rows drive the Discrepancies section breakdown,
  // so fetch whenever the card shows (customer single-manifest reads are
  // authorized by the bulk resolver's waste-module + facility branch).
  const { data: discrepancyData } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: {
      processedManifestIds: processedManifestId ? [processedManifestId] : [],
    },
    skip: !processedManifestId || !showDiscrepancyCard,
  });

  // EPA correction rows are hidden when their flag is off (matches the modal),
  // so the section breakdown and the modal stay in lockstep.
  const showEpaCorrectionRows = !!featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies];
  const sectionDiscrepancies = useMemo(() => {
    // Dismissed non-discrepancies (IGNORED) are not discrepancies — they never
    // count in this summary for anyone. Human-dismissed rows remain reviewable
    // in the staff discrepancy dialog.
    const all = (discrepancyData?.bulkManifestDiscrepancies ?? []).filter(
      (d) => d.status !== DiscrepancyStatus.Ignored
    );
    if (!isStaff) {
      // Customers see Encamp-flagged (field) work in progress + done — pending /
      // resolved, never raw UNRESOLVED. EPA correction requests are the
      // exception: regulator-driven, so customers see them at every open status
      // (including UNRESOLVED), matching the Vendor Stats correction rollup.
      // Missing-e-Manifest compliance stays staff-only.
      return all.filter(
        (d) =>
          (d.kind === DiscrepancyKind.FieldDiscrepancy &&
            d.status !== DiscrepancyStatus.Unresolved) ||
          (showEpaCorrectionRows && d.kind === DiscrepancyKind.EpaCorrectionRequest)
      );
    }
    // The staff detail summary tracks active discrepancies.
    return showEpaCorrectionRows
      ? all
      : all.filter((d) => d.kind !== DiscrepancyKind.EpaCorrectionRequest);
  }, [discrepancyData, showEpaCorrectionRows, isStaff]);

  // Days until the earliest EPA correction deadline (requestDate + 30-day EPA
  // window) across this manifest's open correction requests. Negative = overdue;
  // null when there are no open EPA corrections. Drives the Discrepancies footer.
  const epaCorrectionDueInDays = useMemo(() => {
    const requestDates = sectionDiscrepancies.flatMap((d) => {
      if (d.kind !== DiscrepancyKind.EpaCorrectionRequest) return [];
      if (d.status !== DiscrepancyStatus.Unresolved && d.status !== DiscrepancyStatus.Pending) {
        return [];
      }
      const requestDate = d.wasteManifestCorrectionRequest?.requestDate;
      if (!requestDate) return [];
      return [new Date(requestDate)];
    });
    return daysUntilEarliestEpaCorrectionDeadline(requestDates, new Date());
  }, [sectionDiscrepancies]);

  const proofOfContactDoc = useMemo(
    () =>
      manifestDocuments?.find(
        (doc) => doc.document.documentType === DocumentType.WasteTransporterContactProof
      )?.document ?? null,
    [manifestDocuments]
  );
  const hasProofOfContact = !!proofOfContactDoc;

  // The manifest PDF pulled from RCRAInfo (EPA printed copy), if one was
  // ingested. Distinct from the user-uploaded manifestPdf; surfaced as its own
  // Documents row. Linked via ProcessedManifestDocument, not the document source.
  const rcraInfoPrintedCopy = useMemo(
    () =>
      manifestDocuments?.find(
        (doc) => doc.document.documentType === DocumentType.RcraManifestPrintedCopy
      )?.document ?? null,
    [manifestDocuments]
  );

  const edited =
    !!manifest?.vendorManifest &&
    (manifest.vendorManifest.manuallyEdited ||
      (wasteLinesUnified?.wasteLinesUnified?.items.some((line) => line.manuallyEdited) ?? false));

  // Management method is a per-line value; the Receiving card shows it only when
  // every line agrees, otherwise a "Multiple (N)" summary (see the helper).
  const managementMethod = useMemo(
    () =>
      summarizeManagementMethods(
        (wasteLinesUnified?.wasteLinesUnified?.items ?? []).map(
          (line) => line.managementMethodCode ?? ""
        )
      ),
    [wasteLinesUnified]
  );

  const summaryItems = useMemo(() => {
    if (!manifest) return [];
    const items = buildSummaryItems({
      availableSources,
      siteManagerAccess: manifest.siteManagerAccess,
      isUniformHazardousManifest: manifest.isUniformHazardousManifest,
      // Same compliance-vs-field split the list view's bubbles use, computed
      // from the discrepancy rows the drawer already loaded.
      discrepancyTally: buildDiscrepancyTally(sectionDiscrepancies),
      daysSinceShipment: manifest.daysSinceShipment,
      hasEManifest: manifest.isEManifest,
      escalation: manifest.eManifestComplianceEscalation,
      hasExceptionReport: !!manifest.exceptionReportSubmission,
      exceptionReportFiled:
        manifest.exceptionReportSubmission?.status === SubmissionStatus.FilingComplete,
      reportedWasteGeneratorStatus: manifest.reportedWasteGeneratorStatus,
      contacted: manifest.transporterContactedAt != null || hasProofOfContact,
      shippedDate: manifest.shippedDate,
      eManifestCertifiedDate: manifest.eManifestCertifiedDate,
    });
    return items.filter((item) => {
      // Data sources now render as chips in the Data Sources panel, so drop the
      // redundant summary line (site manager access keeps its line).
      if (item.key === "dataSources") return false;
      if (item.key === "discrepancies") return showDiscrepancyCounts;
      if (item.key.startsWith("compliance")) return showExceptionReporting;
      return true;
    });
  }, [
    manifest,
    availableSources,
    hasProofOfContact,
    showDiscrepancyCounts,
    showExceptionReporting,
    sectionDiscrepancies,
  ]);

  const timelineEvents = useMemo(
    () =>
      manifest
        ? buildTimelineEvents({
            shippedDate: manifest.shippedDate,
            receivedDate: manifest.receivedDate,
            eManifestDaysInTransit: manifest.eManifestDaysInTransit,
            daysSinceShipment: manifest.daysSinceShipment,
            transporters: manifest.wasteTransporters ?? [],
          })
        : [],
    [manifest]
  );

  const [showEditManifestDialog, setShowEditManifestDialog] = useState(false);
  const [showDiscrepancyDialog, setShowDiscrepancyDialog] = useState(false);
  // Which section the discrepancy modal opens to (set by the Discrepancies matrix).
  const [discrepancyDialogSection, setDiscrepancyDialogSection] =
    useState<DiscrepancySectionKey>("epaCorrection");
  const openDiscrepancies = useCallback((section: DiscrepancySectionKey = "epaCorrection") => {
    setDiscrepancyDialogSection(section);
    setShowDiscrepancyDialog(true);
  }, []);
  // Customers click the Discrepancies card through to the small read-only
  // modal (the same one the grid's discrepancy cell opens) instead of the
  // staff review dialog.
  const [showCustomerDiscrepancyModal, setShowCustomerDiscrepancyModal] = useState(false);
  const [showProofOfContactDialog, setShowProofOfContactDialog] = useState(false);
  // Manifest-document review / upload, relocated out of the header into the
  // Documents section (source-document row action + missing-document row).
  const { canQueueManifestExtraction } = useQueueManifestDocumentExtraction();
  // The manifest document (source or a processed sibling) whose review/edit
  // dialog is open; null when closed.
  const [reviewDocId, setReviewDocId] = useState<string | null>(null);
  const [showManifestUploadDialog, setShowManifestUploadDialog] = useState(false);
  const [uploadedManifestDocIds, setUploadedManifestDocIds] = useState<string[] | null>(null);
  // A manifest doc awaiting process confirmation — either a different doc that
  // would replace the current source, or the current source itself being
  // extracted/re-parsed (artifact-only scan or stale source). Everything the
  // confirm dialogs need is snapshotted at click time so a background refetch
  // (the data-source poll, another session detaching/promoting a document)
  // can't swap or vanish an open confirm mid-read.
  const [processDocConfirm, setProcessDocConfirm] = useState<{
    documentId: string;
    documentName: string;
    fileExtension: string | null;
    /** Re-parsing the current source — no replace semantics. */
    isCurrentSource: boolean;
    /** The source document being replaced; null when isCurrentSource. */
    currentDocument: {
      id: string;
      title: string;
      fileExtension: string | null;
      createdAt: string | null;
      reviewed: boolean;
    } | null;
  } | null>(null);
  const canManageManifestDocument = showSourceDocument && canQueueManifestExtraction;
  const manifestSourceDocumentId = manifest?.sourceDocument?.id ?? null;
  const hasManifestDocument = !!manifest?.sourceDocument;
  // True once extraction has produced reviewable metadata for the source
  // document (documentMetadata present ≡ doc intelligence ran). Using this
  // instead of documentManifestId so an artifact-only primary (scan ingested
  // but not yet processed) correctly shows manifestDataExtracted=false and
  // the staff Process action in DocumentsSection.
  const manifestDataExtracted = manifest?.sourceDocument?.documentMetadata != null;
  const manifestDocumentNeedsReview = !!manifest?.sourceDocument?.documentMetadata?.needsReview;
  // True when the source document has been replaced by a newer PDF version since
  // the last review. Drives the staff Refresh action.
  const documentStale = !!manifest?.documentStale;
  // documentStale is denormalized and only recomputed by process-manifests, which
  // runs asynchronously after a Refresh re-processes the new version. To avoid the
  // stale affordance lingering until that catches up, optimistically suppress it
  // the moment Refresh is clicked.
  const [refreshOptimisticallyHidden, setRefreshOptimisticallyHidden] = useState(false);
  // Reset the optimistic suppression whenever the real flag changes (cleared by
  // process-manifests, or a genuinely new version flips it true again). Adjusted
  // during render — not in an effect — per React's "you might not need an effect".
  const [staleAtLastRender, setStaleAtLastRender] = useState(documentStale);
  if (staleAtLastRender !== documentStale) {
    setStaleAtLastRender(documentStale);
    setRefreshOptimisticallyHidden(false);
  }
  const documentStaleVisible = documentStale && !refreshOptimisticallyHidden;

  // Bumped each time an upload/process kicks off the async pipeline, to (re)start
  // the data-source poll below. A nonce (rather than a boolean) lets a second
  // upload re-trigger the poll and avoids adjusting state from inside the effect.
  const [dataSourcePollNonce, setDataSourcePollNonce] = useState(0);
  const triggerDataSourcePoll = useCallback(() => setDataSourcePollNonce((n) => n + 1), []);

  // Refetch only THIS manifest's queries (scoped to its variables) — not the
  // shared query document, which would also refetch the manifests list grid
  // behind the drawer and make it flicker on every poll tick. The suspense
  // refetch runs in a transition so it doesn't re-trigger the drawer's Suspense
  // fallback.
  const refetchThisManifest = useCallback(() => {
    void refetchDocuments();
    startTransition(() => {
      void refetchManifest();
    });
  }, [refetchDocuments, refetchManifest]);

  // The document → data-source link (ProcessedManifest.documentManifestId, which
  // drives sourceDocument) is written by the process-manifests background job,
  // which runs *after* extraction. The uploaded doc already shows immediately via
  // pre-association; this bounded poll catches its promotion to the data source
  // (source dot, review action, pending-review banner) without a manual refresh.
  // Stops once the source links (hasManifestDocument flips the effect off) or
  // after the attempt cap.
  useEffect(() => {
    if (dataSourcePollNonce === 0 || hasManifestDocument) return;
    let attempts = 0;
    const MAX_ATTEMPTS = 24; // ~60s at 2.5s intervals
    const intervalId = setInterval(() => {
      attempts += 1;
      refetchThisManifest();
      if (attempts >= MAX_ATTEMPTS) clearInterval(intervalId);
    }, 2500);
    return () => clearInterval(intervalId);
  }, [dataSourcePollNonce, hasManifestDocument, refetchThisManifest]);

  const { processDocuments } = useProcessManifestDocuments({
    tenantId: tenantId ?? "",
    facilityId: manifest?.facility?.id,
    manifestTrackingNumber: manifest?.manifestTrackingNumber ?? undefined,
    generatorId: manifest?.epaId ?? undefined,
    onReviewReady: setUploadedManifestDocIds,
    onProcessingComplete: triggerDataSourcePoll,
  });

  // Both confirm dialogs share this action; the ref guards a double fire
  // (rapid double-click, or Enter + click) racing ahead of the close render.
  const processConfirmedRef = useRef(false);
  const closeProcessConfirm = useCallback(() => setProcessDocConfirm(null), []);
  const confirmProcessDocument = useCallback(() => {
    if (!processDocConfirm || processConfirmedRef.current) return;
    processConfirmedRef.current = true;
    void processDocuments([processDocConfirm.documentId]);
    setProcessDocConfirm(null);
  }, [processDocConfirm, processDocuments]);

  // Processing a non-source document replaces the data source — confirm first
  // when one already exists (branching on whether it's the current source
  // being re-parsed vs a true replace); otherwise process straight away.
  const handleProcessManifestDocument = useCallback(
    (documentId: string, documentName: string, fileExtension?: string | null) => {
      const sourceDocument = manifest?.sourceDocument;
      if (sourceDocument) {
        const isCurrentSource = documentId === sourceDocument.id;
        processConfirmedRef.current = false;
        setProcessDocConfirm({
          documentId,
          documentName,
          fileExtension: fileExtension ?? null,
          isCurrentSource,
          currentDocument: isCurrentSource
            ? null
            : {
                id: sourceDocument.id,
                title: sourceDocument.title,
                fileExtension: sourceDocument.fileExtension ?? null,
                createdAt: sourceDocument.createdAt ?? null,
                reviewed: isManifestDocumentReviewed(sourceDocument.documentMetadata),
              },
        });
      } else {
        void processDocuments([documentId]);
      }
    },
    [manifest?.sourceDocument, processDocuments]
  );

  // Refresh: re-parse the stale source document (bypasses the canProcess gate
  // because for a stale source canProcess would otherwise be false). Directly
  // queues doc intelligence on the source document rather than going through
  // the replace-source confirmation flow.
  const handleRefreshManifestDocument = useCallback(() => {
    if (manifestSourceDocumentId && manifest?.sourceDocument) {
      void processDocuments([manifestSourceDocumentId]);
      // Hide the stale affordance immediately; process-manifests will clear the
      // real flag a beat later and the effect above keeps the two in sync.
      setRefreshOptimisticallyHidden(true);
    }
  }, [manifestSourceDocumentId, manifest?.sourceDocument, processDocuments]);

  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );
  const [detachDocumentId, setDetachDocumentId] = useState<string | null>(null);
  const [detaching, setDetaching] = useState(false);
  const [railPdf, setRailPdf] = useState<ManifestPdfDocument | null>(null);
  const [getDownloadLink] = useDocumentDownloadLink();
  const [previewState, setPreviewState] = useState<{
    open: boolean;
    fileUrl: string;
    fileExtension: string;
    title: string;
  }>({ open: false, fileUrl: "", fileExtension: "", title: "" });

  const handlePreview = useCallback(
    async (doc: { id: string; title: string; fileExtension?: string | null }) => {
      const { data } = await getDownloadLink({ variables: { id: doc.id } });
      if (data?.getDocumentDownloadLink) {
        setPreviewState({
          open: true,
          fileUrl: data.getDocumentDownloadLink,
          fileExtension: doc.fileExtension ?? "",
          title: doc.title,
        });
      }
    },
    [getDownloadLink]
  );

  const handleDownload = useCallback(
    async (documentId: string) => {
      await fetchAndHandleDownload({ documentId });
      clearDownloadLink();
    },
    [fetchAndHandleDownload, clearDownloadLink]
  );

  // Open the side-by-side Compare Data Sources dialog (M3) via its `?compare=`
  // permalink, which is mounted once on the manifest route (see ManifestDetails).
  const [, setSearchParams] = useSearchParams();
  const openCompareSources = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(COMPARE_PARAM_KEY, encodeCompareParam({ mode: "open" }));
      return next;
    });
  }, [setSearchParams]);

  const [attachDocumentToManifest] = useMutation(ATTACH_DOCUMENT_TO_MANIFEST, {
    refetchQueries: [PROCESSED_MANIFEST_DOCUMENTS_QUERY],
  });
  const [detachDocumentFromManifest] = useMutation(DETACH_DOCUMENT_FROM_MANIFEST, {
    refetchQueries: [PROCESSED_MANIFEST_DOCUMENTS_QUERY],
  });
  const [createExceptionReport, { loading: creatingExceptionReport }] = useMutation(
    CREATE_EXCEPTION_REPORT_SUBMISSION,
    {
      refetchQueries: [PROCESSED_MANIFESTS_QUERY],
      onCompleted: () => alerts.success("Exception report submission created"),
      onError: (err) => alerts.error(`Failed to create exception report: ${err.message}`),
    }
  );

  if (!manifest) {
    return (
      <>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h5">Not Found</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {children}
      </>
    );
  }

  // The PDF rail only appears once a document is explicitly opened (the
  // Documents "eye" action) — no persistent minimized strip. Available to all
  // users (the Documents section is customer-visible), so it can't be gated on
  // the staff-only showDiscrepancyCounts.
  const activePdf = railPdf;
  const showRail = !!activePdf;

  // Compliance (left) + Discrepancies (right) sit side by side. Both cards are
  // customer-visible on manifest-views-v2 tenants so the pair fills the row —
  // customers get a read-only compliance card (status + timeline, no staff
  // actions), which also keeps the Discrepancies card from stretching to full
  // width alone. showDiscrepancyCard is declared with the other gates up top (it
  // also drives the discrepancy-rows query).
  const showComplianceCard = showDiscrepancyCard;
  // The compliance card shows exception-report status to customers too (they
  // already see it in the grid's e-Manifest column); the staff-only actions are
  // gated inside ComplianceSection via readOnly, not by hiding the status.
  const showComplianceCardExceptions = !!featureFlags[FeatureFlagKey.WasteExceptionReporting];

  return (
    <>
      <StickyManifestHeader
        manifest={manifest}
        isStaff={isStaff}
        edited={edited}
        onEdit={() => setShowEditManifestDialog(true)}
        onClose={onClose}
      />
      {showEditManifestDialog && (
        <EditManifestDialog
          onClose={() => setShowEditManifestDialog(false)}
          manifest={manifest}
          onDeleteSuccess={() => {
            setShowEditManifestDialog(false);
            onClose();
          }}
        />
      )}

      {/* Manifest-document review/upload, triggered from the Documents section.
          reviewDocId is the source doc OR a processed sibling; the dialog resolves
          the sibling's pillar from the document, so wasteManifestId is only passed
          for the source (preserving its prior behavior). */}
      {reviewDocId && (
        <ManifestReviewDialog
          onClose={() => setReviewDocId(null)}
          documentIds={[reviewDocId]}
          wasteManifestId={
            reviewDocId === manifest.sourceDocument?.id && manifestDataExtracted
              ? (manifest.documentManifestId ?? undefined)
              : undefined
          }
        />
      )}
      {showManifestUploadDialog && (
        <ManifestUploadDialog
          open
          onClose={() => setShowManifestUploadDialog(false)}
          tenantId={tenantId ?? ""}
          facilityId={manifest.facility?.id}
          manifestTrackingNumber={manifest.manifestTrackingNumber ?? undefined}
          generatorId={manifest.epaId ?? undefined}
          processedManifestId={manifest.id}
          onExtractionReady={setUploadedManifestDocIds}
          onProcessingComplete={triggerDataSourcePoll}
        />
      )}
      {uploadedManifestDocIds && (
        <ManifestReviewDialog
          onClose={() => setUploadedManifestDocIds(null)}
          documentIds={uploadedManifestDocIds}
        />
      )}
      <ConfirmDialog
        open={processDocConfirm?.isCurrentSource === true}
        header="Process manifest document data?"
        info="Processing this document extracts its manifest data into the manifest. The document won't be removed."
        confirmText="Process Document"
        onClose={closeProcessConfirm}
        onConfirm={confirmProcessDocument}
      />
      {/* A true replace (different document) gets the richer dialog: preview of
          the current source PDF with a Current/New toggle, plus the
          discarded-review warning. Rendered from the click-time
          snapshot, not the live manifest. */}
      {processDocConfirm?.currentDocument && (
        <ReplaceManifestDocumentDialog
          open
          currentDocument={processDocConfirm.currentDocument}
          newDocument={{
            id: processDocConfirm.documentId,
            title: processDocConfirm.documentName,
            fileExtension: processDocConfirm.fileExtension,
          }}
          onClose={closeProcessConfirm}
          onConfirm={confirmProcessDocument}
        />
      )}

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Stack spacing={3} px={3} py={2} sx={{ flex: 1, overflow: "auto" }}>
          <Stack
            direction={showRail ? "column" : { xs: "column", md: "row" }}
            spacing={3}
            alignItems="stretch"
          >
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <SectionCard>
                <SummarySection
                  items={summaryItems}
                  availableSources={availableSources}
                  // A document pillar absent from availableSources is unreviewed
                  // (fail-closed — flagged for review, never reviewed, or
                  // extraction failed). Surfaced to all users via the Data
                  // Sources "pending review" marker; the banner below carries
                  // the explainer + View action. Derived from availability so it
                  // can't drift from the backend's review gate.
                  documentPendingReview={documentDataUnreviewed}
                  // Clicking the Data Sources pending-review marker opens the
                  // same document-review screen as the Documents section's
                  // Review action — staff-only, and only when a source document
                  // exists to review.
                  onReviewDocument={
                    canManageManifestDocument && manifestSourceDocumentId
                      ? () => setReviewDocId(manifestSourceDocumentId)
                      : undefined
                  }
                />
                {/* A document is attached but its data is gated until Encamp
                    reviews it. Make that clear to all users (not just staff) and
                    keep the document itself one click away. Gate on manifestPdf
                    (from the separate documents query) so the "viewable" copy is
                    only shown when there's a loaded document behind the View
                    action — the Data Sources "pending review" marker covers the
                    brief pre-load window. The data gate itself lives in the
                    backend; this only explains it. */}
                {documentDataUnreviewed && manifestPdf && (
                  <Alert
                    severity="info"
                    variant="outlined"
                    sx={{ mt: 2 }}
                    action={
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<Visibility fontSize="small" />}
                        onClick={() => setRailPdf(manifestPdf)}
                      >
                        View document
                      </Button>
                    }
                  >
                    The attached manifest document is viewable, but its data won&apos;t appear as
                    manifest record data until the document is reviewed by Encamp staff.
                  </Alert>
                )}
                {warnMissingEManifestSiteAccess && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
                    This manifest may already be in e-Manifest, but without RCRA Site Manager access
                    for this EPA ID we can&apos;t retrieve it. It will become available in the
                    public export about 90 days after shipment. Contact your Encamp rep to correct
                    this issue.
                  </Alert>
                )}
              </SectionCard>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <SectionCard>
                <TimelineSection events={timelineEvents} />
              </SectionCard>
            </Box>
          </Stack>

          {/* Discrepancies (left) + Compliance (right), side by side like
              Summary/Timeline. Both render together on manifest-views-v2 tenants
              (staff and customers), so neither is left alone to stretch the row. */}
          {(showComplianceCard || showDiscrepancyCard) && (
            <Stack
              direction={showRail ? "column" : { xs: "column", md: "row" }}
              spacing={3}
              alignItems="stretch"
            >
              {showDiscrepancyCard && (
                <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                  <SectionCard>
                    <DiscrepanciesSection
                      discrepancies={sectionDiscrepancies}
                      onOpenDiscrepancies={
                        isStaff ? openDiscrepancies : () => setShowCustomerDiscrepancyModal(true)
                      }
                      epaCorrectionDueInDays={epaCorrectionDueInDays}
                      canReview={isStaff}
                      customerView={!isStaff}
                    />
                  </SectionCard>
                </Box>
              )}
              {showComplianceCard && (
                <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                  <SectionCard>
                    <ComplianceSection
                      manifest={manifest}
                      tenantId={tenantId ?? ""}
                      readOnly={!isStaff}
                      showExceptionReporting={showComplianceCardExceptions}
                      hasProofOfContact={hasProofOfContact}
                      creatingExceptionReport={creatingExceptionReport}
                      onAddProofOfContact={() => setShowProofOfContactDialog(true)}
                      onCreateExceptionReport={() =>
                        createExceptionReport({
                          variables: { processedManifestId: manifest.id },
                        })
                      }
                      onViewProofOfContact={
                        proofOfContactDoc ? () => handlePreview(proofOfContactDoc) : undefined
                      }
                    />
                  </SectionCard>
                </Box>
              )}
            </Stack>
          )}
          {showDiscrepancyDialog && (
            <DiscrepancyReviewDialog
              processedManifestIds={[manifest.id]}
              tenantId={tenantId}
              manifestTrackingNumber={
                manifest.vendorManifest?.manifestTrackingNumber ?? manifest.manifestTrackingNumber
              }
              initialSection={discrepancyDialogSection}
              onClose={() => setShowDiscrepancyDialog(false)}
            />
          )}
          {/* Customer click-through from the Discrepancies card: the same small
              read-only modal the grid's discrepancy cell opens (pending +
              resolved field discrepancies only). */}
          {showCustomerDiscrepancyModal && (
            <ManifestDetailDialog
              mode="customer"
              customerView
              open
              onClose={() => setShowCustomerDiscrepancyModal(false)}
              processedManifestId={manifest.id}
              manifestTrackingNumber={
                manifest.vendorManifest?.manifestTrackingNumber ?? manifest.manifestTrackingNumber
              }
              facilityName={manifest.facility?.name ?? null}
            />
          )}

          {/* Source-agnostic manifest documents — sit above the Data source
              group. Shown to ALL users: customers view/download the manifest PDF
              and supporting docs, and (per the attach/detach mutations' auth)
              facility-permissioned customers can attach/detach. Staff-only
              affordances (Review / Process / Upload manifest data) are gated
              inside DocumentsSection via canManageManifestDocument. */}
          <Stack ref={documentsSectionRef} spacing={3}>
            <SectionCard>
              <DocumentsSection
                uploadedPdf={manifestPdf}
                rcraInfoApiPdf={rcraInfoPrintedCopy}
                additionalManifestDocuments={additionalManifestDocuments ?? undefined}
                supportingDocuments={manifestDocuments ?? undefined}
                exceptionReportDocuments={exceptionReportDocuments ?? undefined}
                documentsError={!!documentsError}
                loading={documentsLoading}
                showExceptionReporting={!!showExceptionReporting}
                onAttach={() => {
                  setShowAttachDialog(true);
                  setAddEditDialogState({
                    ...defaultAddEditDocumentDialogState,
                    open: true,
                    mode: EditMode.ADD,
                    step: FormState.UPLOAD,
                  });
                }}
                onPreviewSupporting={handlePreview}
                onDownload={handleDownload}
                onDetach={setDetachDocumentId}
                onViewPdf={(document) =>
                  setRailPdf((cur) => (cur?.id === document.id ? null : document))
                }
                previewingDocumentId={activePdf?.id ?? null}
                sourceDocumentId={manifestSourceDocumentId}
                manifestDataExtracted={manifestDataExtracted}
                manifestDocumentNeedsReview={manifestDocumentNeedsReview}
                hasManifestDocument={hasManifestDocument}
                canManageManifestDocument={canManageManifestDocument}
                canDetach={isStaff}
                documentPendingReview={documentDataUnreviewed}
                onReviewManifestDocument={(documentId) => setReviewDocId(documentId)}
                onUploadManifestDocument={() => setShowManifestUploadDialog(true)}
                onProcessManifestDocument={handleProcessManifestDocument}
                documentStale={documentStaleVisible}
                onRefreshManifestDocument={handleRefreshManifestDocument}
              />
            </SectionCard>

            <ConfirmDialog
              open={detachDocumentId != null}
              header="Detach document"
              info="Are you sure you want to detach this document from the manifest?"
              confirmText="Yes, Detach"
              loading={detaching}
              onClose={() => setDetachDocumentId(null)}
              onConfirm={async () => {
                if (detachDocumentId == null) return;
                setDetaching(true);
                try {
                  await detachDocumentFromManifest({
                    variables: {
                      processedManifestDocumentId: detachDocumentId,
                    },
                  });
                  alerts.success("Document detached from manifest");
                  setDetachDocumentId(null);
                } catch (err) {
                  alerts.error("Failed to detach document");
                } finally {
                  setDetaching(false);
                }
              }}
            />

            {showAttachDialog && tenantId && (
              <Stack spacing={1} sx={{ mb: 1 }}>
                <AddEditDocumentDialog
                  tenantId={tenantId}
                  dialogState={addEditDialogState}
                  setDialogState={(stateOrUpdater) => {
                    setAddEditDialogState(stateOrUpdater);
                    // If dialog is being closed, also close the attach flow
                    if (typeof stateOrUpdater === "object" && !stateOrUpdater.open) {
                      setShowAttachDialog(false);
                    }
                  }}
                  documentTypeOptions={[
                    DocumentType.RcraManifest,
                    DocumentType.WasteTransporterContactProof,
                    DocumentType.WasteExceptionReportProof,
                    DocumentType.WasteShippingDocument,
                    DocumentType.Other,
                  ]}
                  showFacilityPicker={false}
                  onSubmit={async (document) => {
                    try {
                      await attachDocumentToManifest({
                        variables: {
                          processedManifestId: manifest.id,
                          documentId: document.id,
                        },
                      });
                      alerts.success("Document attached to manifest");
                      setShowAttachDialog(false);
                    } catch (err) {
                      alerts.error("Failed to attach document to manifest");
                    }
                  }}
                  refetchQueries={[]}
                />
              </Stack>
            )}
          </Stack>

          {/* The manifest's data, shown from its primary source (fields fall
              back to the next available source, marked with a dot). */}
          <Box>
            <SectionCard>
              <SectionHeader
                title="Manifest data"
                action={
                  // Staff-only, matching the per-field "Open compare" links below.
                  showDiscrepancyCounts ? (
                    <Button
                      size="small"
                      startIcon={<CompareArrowsIcon />}
                      onClick={openCompareSources}
                    >
                      Compare data sources
                    </Button>
                  ) : undefined
                }
              />
              {/* Primary source, in muted text tucked right under the header.
                Hidden when NO source is available (e.g. the only pillar is an
                unreviewed document) — defaultSource falls back to e-Manifest
                for component plumbing, and claiming that here would fabricate
                provenance that doesn't exist. */}
              {availableSources.length > 0 && (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: -1, mb: 2 }}>
                  <SourceLetterBox source={primarySource} size={15} tooltip={null} />
                  <Typography variant="caption" color="text.secondary">
                    Primary source: {SOURCE_LABEL[primarySource]}
                  </Typography>
                </Stack>
              )}
              <Stack spacing={2}>
                <Stack
                  direction={showRail ? "column" : { xs: "column", md: "row" }}
                  spacing={hasTransporters ? 2 : 3}
                  alignItems="stretch"
                >
                  <GeneratorFacilitySection
                    manifest={manifest}
                    source={primarySource}
                    availableSources={availableSources}
                    valuesByField={valuesByField}
                    disagreementFields={disagreementFields}
                    onOpenCompare={showDiscrepancyCounts ? openCompareSources : undefined}
                    narrow={hasTransporters && !showRail}
                  />
                  <FlowArrow showRail={showRail} />
                  {hasTransporters && (
                    <>
                      <TransportersSection
                        transporters={manifest.wasteTransporters ?? []}
                        primarySource={primarySource}
                      />
                      <FlowArrow showRail={showRail} />
                    </>
                  )}
                  <ReceivingTsdfSection
                    manifest={manifest}
                    source={primarySource}
                    availableSources={availableSources}
                    valuesByField={valuesByField}
                    disagreementFields={disagreementFields}
                    managementMethod={managementMethod}
                    onOpenCompare={showDiscrepancyCounts ? openCompareSources : undefined}
                    narrow={hasTransporters && !showRail}
                  />
                </Stack>

                {/* Line items reflect the manifest's primary source. minWidth:0 +
                  overflow keeps the DataGrid scrolling inside the card instead of
                  pushing past it (matches the sibling section boxes). */}
                <Box sx={{ minWidth: 0, width: "100%", overflow: "auto" }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Line items
                  </Typography>
                  {children}
                </Box>
              </Stack>
            </SectionCard>
          </Box>

          {/* Container tracking's view of this manifest: which containers it
              carried, or which open shipments it might belong to. Renders itself
              away entirely when the tenant is not on container tracking, or when
              there is neither a link nor a plausible candidate. */}
          {processedManifestId && (
            <ManifestLinkedShipmentCard
              processedManifestId={processedManifestId}
              tenantId={tenantId}
            />
          )}

          {showProofOfContactDialog && (
            <ProofOfContactDialog
              open={showProofOfContactDialog}
              onClose={() => setShowProofOfContactDialog(false)}
              processedManifestId={manifest.id}
              tenantId={tenantId}
            />
          )}
        </Stack>

        {/* Animate the rail sliding in/out by transitioning its width; the
            content column flexes into the remaining space. */}
        <Box
          sx={{
            flexShrink: 0,
            overflow: "hidden",
            width: showRail ? "45%" : 0,
            // easeInOut over the standard 225ms enter duration.
            transition: "width 225ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {showRail && activePdf && (
            <ManifestPdfRail document={activePdf} onClose={() => setRailPdf(null)} />
          )}
        </Box>
      </Box>

      <FullScreenDocumentPreview
        open={previewState.open}
        onClose={() => setPreviewState((prev) => ({ ...prev, open: false }))}
        fileUrl={previewState.fileUrl}
        fileExtension={previewState.fileExtension}
        title={previewState.title}
      />
    </>
  );
};

export const ManifestDetailSectionSkeleton = () => {
  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack spacing={0.5} minWidth={0}>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="text" width={220} height={20} />
        </Stack>
        <Skeleton variant="circular" width={32} height={32} />
      </Box>

      <Stack spacing={3} px={3} py={2} overflow="auto">
        {/* Summary + Timeline cards */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Skeleton variant="rounded" height={150} sx={{ flex: 1 }} />
          <Skeleton variant="rounded" height={150} sx={{ flex: 1 }} />
        </Stack>

        {/* Documents card */}
        <Skeleton variant="rounded" height={140} />

        {/* Manifest data card: Generator → Transporters → Receiving flow, then line items */}
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Skeleton variant="rounded" height={220} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={220} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={220} sx={{ flex: 1 }} />
          </Stack>
          <Skeleton variant="rounded" height={260} />
        </Stack>
      </Stack>
    </>
  );
};
